import { z } from "zod";
import { BearingDeg, LatLon, SpeedKnots } from "./geometry.js";
import { EnvironmentState, TimedEvent, Vessel } from "./world.js";

export const Area = z.enum([
  "solent",
  "weymouth-portland",
  "moray-firth",
  "plockton",
  "uk-general",
]);
export type Area = z.infer<typeof Area>;

export const ScenarioMetadata = z.object({
  /** Stable kebab-case slug, immutable once published. */
  id: z
    .string()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "id must be kebab-case ASCII"),
  name: z.string().min(1),
  description: z.string().min(1),
  /** RYA G34 syllabus item references, e.g. "g34.collision-avoidance.crossing". */
  syllabusTags: z.array(z.string().min(1)).min(1),
  area: Area,
  difficulty: z.number().int().gte(1).lte(5),
  expectedDurationMinutes: z.number().int().positive(),
  author: z.string().min(1),
  /** Semver of the scenario itself; bumps when the scenario changes meaningfully. */
  scenarioVersion: z
    .string()
    .regex(/^\d+\.\d+\.\d+$/, "scenarioVersion must be semver"),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type ScenarioMetadata = z.infer<typeof ScenarioMetadata>;

export const InitialConditions = z.object({
  ownShip: z.object({
    position: LatLon,
    cog: BearingDeg,
    sog: SpeedKnots,
    heading: BearingDeg,
  }),
  /** ISO datetime with offset; drives tide phase, daylight, time-of-day labelling. */
  simStartWallClock: z.string().datetime({ offset: true }),
  environment: EnvironmentState,
});
export type InitialConditions = z.infer<typeof InitialConditions>;

export const SuccessCriterion = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("acquire_target"),
    vesselId: z.string(),
    withinSeconds: z.number().positive().optional(),
  }),
  z.object({
    kind: z.literal("maintain_cpa_above"),
    nm: z.number().positive(),
    vesselId: z.string().optional(),
  }),
  z.object({
    kind: z.literal("complete_within"),
    seconds: z.number().positive(),
  }),
  z.object({
    kind: z.literal("recover_mob"),
    withinSeconds: z.number().positive(),
    toleranceNm: z.number().positive(),
  }),
  z.object({
    kind: z.literal("identify_target"),
    vesselId: z.string(),
    correctTypeRequired: z.boolean(),
  }),
]);
export type SuccessCriterion = z.infer<typeof SuccessCriterion>;

/**
 * Current scenario schema version. Bumps are reserved for breaking changes.
 * Additive changes (new optional fields, new event kinds, new behaviours)
 * do NOT bump this.
 */
export const SCHEMA_VERSION = 1 as const;

export const Scenario = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    metadata: ScenarioMetadata,
    initialConditions: InitialConditions,
    vessels: z.array(Vessel),
    events: z.array(TimedEvent),
    successCriteria: z.array(SuccessCriterion).min(1),
  })
  .superRefine((scenario, ctx) => {
    // Vessel ids must be unique within a scenario.
    const ids = new Set<string>();
    for (const v of scenario.vessels) {
      if (ids.has(v.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["vessels"],
          message: `duplicate vessel id: ${v.id}`,
        });
      }
      ids.add(v.id);
    }
    // success criteria referencing vessels must point at known ids
    for (const [i, c] of scenario.successCriteria.entries()) {
      if ("vesselId" in c && c.vesselId && !ids.has(c.vesselId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["successCriteria", i, "vesselId"],
          message: `unknown vessel id: ${c.vesselId}`,
        });
      }
    }
    // events referencing vessels must point at known ids
    for (const [i, te] of scenario.events.entries()) {
      const ev = te.event;
      if (ev.kind === "vessel_disappears" && !ids.has(ev.vesselId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["events", i, "event", "vesselId"],
          message: `unknown vessel id: ${ev.vesselId}`,
        });
      }
      if (ev.kind === "vessel_appears") {
        // vessel_appears is allowed to introduce a new vessel; record its id
        ids.add(ev.vessel.id);
      }
    }
  });
export type Scenario = z.infer<typeof Scenario>;
